import { Alert, Button, Link as MuiLink, Paper, Stack, Typography } from '@mui/material';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router';
import type { TFunction } from 'i18next';

import { useAuth } from '../../auth/AuthProvider';
import type { DataSource } from '../../config/capabilities';
import { useRepository } from '../../data/RepositoryProvider';
import { useMutation } from '../../hooks/useMutation';
import { useResource } from '../../hooks/useResource';
import { SensitiveActionDialog } from '../../shared/actions/SensitiveActionDialog';
import { PageState } from '../../shared/data/PageState';
import { DetailHeader } from '../../shared/detail/DetailHeader';
import { MetadataList } from '../../shared/detail/MetadataList';
import { formatTimestamp, type TimeLanguage } from '../../shared/format/time';
import { commissionRulesRepositoryToken } from './repository';
import { RuleStatusChip } from './CommissionRuleListPage';
import { toSafeError } from './CommissionRuleWizard';
import type { CommissionRuleDetail } from './types';

type LifecycleAction = 'publish' | 'cancel';

interface Intent {
  readonly action: LifecycleAction;
  readonly idempotencyKey: string;
}

export function CommissionRuleDetailPage({ dataSource }: { readonly dataSource: DataSource }) {
  const { ruleId = '' } = useParams();
  const repository = useRepository(commissionRulesRepositoryToken);
  const auth = useAuth();
  const { i18n, t } = useTranslation();
  const language: TimeLanguage = i18n.language.startsWith('th') ? 'th' : 'en';
  const [refreshKey, setRefreshKey] = useState(0);
  const [intent, setIntent] = useState<Intent | null>(null);
  const lifecycleMutation = useMutation(
    ({ action, reason, idempotencyKey }: Intent & { readonly reason: string }) =>
      action === 'publish'
        ? repository.publish(ruleId, { reason }, idempotencyKey)
        : repository.cancel(ruleId, { reason }, idempotencyKey),
  );
  const resource = useResource(
    (signal) => repository.get(ruleId, signal),
    [repository, ruleId, refreshKey],
  );
  const permissions = auth.status === 'authenticated' ? auth.principal.permissions : [];

  const openIntent = (action: LifecycleAction) => {
    lifecycleMutation.reset();
    setIntent({ action, idempotencyKey: crypto.randomUUID() });
  };
  const closeIntent = () => {
    lifecycleMutation.reset();
    setIntent(null);
  };

  return (
    <Stack spacing={2} sx={{ minWidth: 0 }}>
      <PageState
        error={
          resource.error === null
            ? null
            : toSafeError(resource.error, t('commissionRules.errors.detail'), t)
        }
        onRetry={() => setRefreshKey((key) => key + 1)}
        status={resource.status}
      >
        {resource.data === null ? null : (
          <RuleDetail
            dataSource={dataSource}
            language={language}
            onCancel={() => openIntent('cancel')}
            onPublish={() => openIntent('publish')}
            permissions={permissions}
            rule={resource.data}
          />
        )}
      </PageState>
      {intent === null || resource.data === null ? null : (
        <SensitiveActionDialog
          impact={t(`commissionRules.lifecycle.${intent.action}Impact`, {
            id: resource.data.id,
            current: resource.data.lifecycle,
            next: intent.action === 'publish' ? 'PUBLISHED' : 'CANCELLED',
          })}
          onClose={closeIntent}
          onConfirm={async ({ reason }) => {
            try {
              await lifecycleMutation.mutate({ ...intent, reason });
              setRefreshKey((key) => key + 1);
            } catch (error) {
              throw toSafeError(error, t('commissionRules.errors.lifecycle'), t);
            }
          }}
          open
          requireEvidence={false}
          showEvidence={false}
          title={t(`commissionRules.lifecycle.${intent.action}Title`)}
        />
      )}
    </Stack>
  );
}

function RuleDetail({
  dataSource,
  language,
  onCancel,
  onPublish,
  permissions,
  rule,
}: {
  readonly dataSource: DataSource;
  readonly language: TimeLanguage;
  readonly onCancel: () => void;
  readonly onPublish: () => void;
  readonly permissions: readonly string[];
  readonly rule: CommissionRuleDetail;
}) {
  const { t } = useTranslation();
  const canPublish = rule.lifecycle === 'DRAFT' && permissions.includes('COMMISSION_RULE_APPROVE');
  const canCancel =
    permissions.includes('COMMISSION_RULE_MANAGE') &&
    (rule.lifecycle === 'DRAFT' || (rule.lifecycle === 'PUBLISHED' && rule.phase === 'SCHEDULED'));
  return (
    <>
      <DetailHeader
        actions={
          <>
            {canPublish ? (
              <Button onClick={onPublish} variant="contained">
                {t('commissionRules.actions.publish')}
              </Button>
            ) : null}
            {canCancel ? (
              <Button color="error" onClick={onCancel} variant="outlined">
                {t('commissionRules.actions.cancel')}
              </Button>
            ) : null}
          </>
        }
        backTo="/commission-rules"
        dataSource={dataSource}
        subtitle={t('commissionRules.fields.versionValue', { version: rule.version })}
        title={rule.id}
      />
      <Alert severity="info" sx={{ mb: 2 }}>
        {t('commissionRules.detail.immutable')}
      </Alert>
      <Paper component="section" variant="outlined" sx={{ p: 2 }}>
        <Typography component="h2" variant="h5" sx={{ mb: 2 }}>
          {t('commissionRules.detail.configuration')}
        </Typography>
        <MetadataList items={detailItems(rule, language, t)} />
      </Paper>
      <Paper component="section" variant="outlined" sx={{ mt: 2, p: 2 }}>
        <Typography component="h2" variant="h5" sx={{ mb: 2 }}>
          {t('commissionRules.detail.dependencies')}
        </Typography>
        {rule.dependencyBlockers.length === 0 ? (
          <Typography>{t('commissionRules.detail.noDependencies')}</Typography>
        ) : (
          <Stack component="ul">
            {rule.dependencyBlockers.map((code) => (
              <Typography component="li" key={code}>
                {safeDependency(code, t)}
              </Typography>
            ))}
          </Stack>
        )}
      </Paper>
    </>
  );
}

function detailItems(rule: CommissionRuleDetail, language: TimeLanguage, t: TFunction) {
  const timestamp = (value: string | null) => {
    if (value === null) return t('common.notAvailable');
    const formatted = formatTimestamp(value, 'Asia/Bangkok', language);
    return `${formatted.display} (${formatted.originalOffset})`;
  };
  return [
    { label: t('commissionRules.fields.scope'), value: rule.scope },
    {
      label: t('commissionRules.fields.partnerId'),
      value:
        rule.partnerId === null ? (
          t('common.notAvailable')
        ) : (
          <MuiLink component={Link} to={`/partners/${rule.partnerId}`}>
            {rule.partnerId}
          </MuiLink>
        ),
    },
    {
      label: t('commissionRules.fields.game'),
      value: (
        <MuiLink component={Link} to={`/games/${rule.gameId}`}>
          {rule.game.code} · {rule.game.name}
        </MuiLink>
      ),
    },
    { label: t('commissionRules.fields.activationMode'), value: rule.activationMode },
    { label: t('commissionRules.fields.agentRatePercent'), value: rule.agentRatePercent },
    { label: t('commissionRules.fields.affiliateRatePercent'), value: rule.affiliateRatePercent },
    { label: t('commissionRules.fields.effectiveFrom'), value: timestamp(rule.effectiveFrom) },
    { label: t('commissionRules.fields.effectiveTo'), value: timestamp(rule.effectiveTo) },
    {
      label: t('commissionRules.fields.lifecycle'),
      value: <RuleStatusChip value={rule.lifecycle} />,
    },
    { label: t('commissionRules.fields.phase'), value: <RuleStatusChip value={rule.phase} /> },
    {
      label: t('commissionRules.fields.effective'),
      value: rule.effective ? t('common.yes') : t('common.no'),
    },
    { label: t('commissionRules.fields.createdAt'), value: timestamp(rule.createdAt) },
    { label: t('commissionRules.fields.createdBy'), value: rule.createdBy },
    { label: t('commissionRules.fields.publishedAt'), value: timestamp(rule.publishedAt) },
    {
      label: t('commissionRules.fields.approvedBy'),
      value: rule.publishedBy ?? t('common.notAvailable'),
    },
    { label: t('commissionRules.fields.cancelledAt'), value: timestamp(rule.cancelledAt) },
    {
      label: t('commissionRules.fields.cancelledBy'),
      value: rule.cancelledBy ?? t('common.notAvailable'),
    },
    {
      label: t('commissionRules.fields.cancellationReason'),
      value: rule.cancellationReason ?? t('common.notAvailable'),
    },
  ];
}

function safeDependency(code: string, t: TFunction): string {
  return code === 'REFERENCED_BY_TRANSACTIONS'
    ? t('commissionRules.dependencies.referencedByTransactions')
    : t('commissionRules.dependencies.fallback');
}
